'use client';

import { useState } from 'react';

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookDemoModal({ isOpen, onClose }: BookDemoModalProps) {
  const [step, setStep] = useState<'form' | 'calendar'>('form');
  const [formData, setFormData] = useState({ name: '', email: '', company: '', useCase: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/demo/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setStep('calendar');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl border border-[#C9A961]/30 bg-[#0F172A] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 p-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-white">Book a Demo</h2>
            <p className="text-sm text-gray-400">See ChronoMind™ in action with our team</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {step === 'form' ? (
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-300">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-[#C9A961]/40 focus:outline-none"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-300">Work Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-[#C9A961]/40 focus:outline-none"
                    placeholder="jane@company.com"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">Company *</label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-[#C9A961]/40 focus:outline-none"
                  placeholder="Acme Corporation"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">Primary Use Case</label>
                <select
                  value={formData.useCase}
                  onChange={(e) => setFormData({ ...formData, useCase: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-[#0F172A] px-3 py-2.5 text-sm text-white focus:border-[#C9A961]/40 focus:outline-none"
                >
                  <option value="">Select your primary need...</option>
                  <option value="EU AI Act Compliance">EU AI Act Compliance</option>
                  <option value="ISO 42001 Certification">ISO 42001 Certification</option>
                  <option value="AI Risk Management">AI Risk Management</option>
                  <option value="Board Reporting">Board Reporting</option>
                  <option value="Federal/Government">Federal/Government</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-[#C9A961] py-3 text-sm font-bold text-[#0F172A] hover:bg-[#e8c97a] disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Submitting...' : 'Continue to Schedule →'}
              </button>
            </form>
          ) : (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20 border border-green-500/30">
                <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">Thanks, {formData.name.split(' ')[0]}!</h3>
              <p className="text-gray-400 text-sm mb-6">Our team will reach out to <strong className="text-white">{formData.email}</strong> within 24 hours to schedule your personalized demo.</p>
              <div className="rounded-xl border border-[#C9A961]/20 bg-[#C9A961]/5 p-4 mb-6">
                <p className="text-sm text-[#C9A961] font-medium">What to expect in your demo:</p>
                <ul className="mt-2 space-y-1 text-xs text-gray-400 text-left">
                  <li>• Live walkthrough of the CMRI dashboard</li>
                  <li>• EU AI Act compliance mapping demo</li>
                  <li>• AI Governance Assistant in action</li>
                  <li>• Custom pricing for your organization</li>
                </ul>
              </div>
              <button onClick={onClose} className="rounded-lg border border-white/20 px-6 py-2.5 text-sm font-medium text-gray-300 hover:bg-white/5 transition-colors">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
